import { array } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismLoadingBar,
  PrismMessage,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import SettingsBudgetForecastModalsModalCopyToExisting from 'lumen/components/settings/budget-forecast/modals/modal-copy-to-existing';
import SettingsBudgetForecastModalsModalCreateEntry from 'lumen/components/settings/budget-forecast/modals/modal-create-entry';
import SettingsBudgetForecastModalsModalDeleteEntry from 'lumen/components/settings/budget-forecast/modals/modal-delete-entry';
import SettingsBudgetForecastModalsModalDuplicateEntry from 'lumen/components/settings/budget-forecast/modals/modal-duplicate-entry';
import SettingsBudgetForecastModalsModalEditEntryTitle from 'lumen/components/settings/budget-forecast/modals/modal-edit-entry-title';
import SettingsBudgetForecastTableEntries from 'lumen/components/settings/budget-forecast/table-entries';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type { BudgetForecastEntry } from 'lumen/models';

export interface SettingsBudgetForecastSettingsIndexPageSignature {
  Args: {
    entries: BudgetForecastEntry[];
    isLoading: boolean;
    view: 'budget' | 'forecast';
  };
}

const TABS = {
  ACTIVE: 'active',
  ARCHIVE: 'archive',
} as const;
type TabId = (typeof TABS)[keyof typeof TABS];
const isValidTabId = (tabId: string): tabId is TabId =>
  Object.values(TABS).includes(tabId as TabId);

export default class SettingsBudgetForecastSettingsIndexPage extends Component<SettingsBudgetForecastSettingsIndexPageSignature> {
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked activeTabId: TabId = 'active';
  @tracked duplicateModalOptions: {
    entry: BudgetForecastEntry;
    type: 'duplicate' | 'duplicate-as-other-type';
  } | null = null;
  @tracked entryBeingCopiedToExisting: BudgetForecastEntry | null = null;
  @tracked entryBeingDeleted: BudgetForecastEntry | null = null;
  @tracked entryBeingTitleEdited: BudgetForecastEntry | null = null;
  @tracked isCreateEntryModalOpen = false;

  archiveEntryTask = task(async (entry: BudgetForecastEntry): Promise<void> => {
    try {
      entry.isArchived = true;
      await entry.save({
        adapterOptions: {
          usePatch: true,
        },
      });

      this.toast.success(
        this.intl.t('budget_forecast.archive_entry.success', {
          title: entry.title,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  lockEntryTask = task(async (entry: BudgetForecastEntry): Promise<void> => {
    try {
      entry.isLocked = true;
      await entry.save({
        adapterOptions: {
          usePatch: true,
        },
      });

      this.toast.success(
        this.intl.t('budget_forecast.lock_entry.success', {
          title: entry.titleWithDefault,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  restoreEntryTask = task(async (entry: BudgetForecastEntry): Promise<void> => {
    try {
      entry.isArchived = false;
      await entry.save({
        adapterOptions: {
          usePatch: true,
        },
      });

      this.toast.success(
        this.intl.t('budget_forecast.restore_entry.success', {
          title: entry.title,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  unlockEntryTask = task(async (entry: BudgetForecastEntry): Promise<void> => {
    try {
      entry.isLocked = false;
      await entry.save({
        adapterOptions: {
          usePatch: true,
        },
      });

      this.toast.success(
        this.intl.t('budget_forecast.unlock_entry.success', {
          title: entry.titleWithDefault,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  get activeEntries(): BudgetForecastEntry[] {
    return this.args.entries.filter((entry) => !entry.isArchived);
  }

  get archivedEntries(): BudgetForecastEntry[] {
    return this.args.entries.filter((entry) => entry.isArchived);
  }

  @action
  onCloseCopyToExistingModal(): void {
    this.entryBeingCopiedToExisting = null;
  }

  @action
  onCloseCreateEntryModal(): void {
    this.isCreateEntryModalOpen = false;
  }

  @action
  onCloseDeleteEntryModal(): void {
    this.entryBeingDeleted = null;
  }

  @action
  onCloseDuplicateModal(): void {
    this.duplicateModalOptions = null;
  }

  @action
  onCloseEditTitleModal(): void {
    this.entryBeingTitleEdited = null;
  }

  @action
  onCopyToExisting(entry: BudgetForecastEntry): void {
    this.entryBeingCopiedToExisting = entry;
  }

  @action
  onDuplicateAs(entry: BudgetForecastEntry): void {
    this.duplicateModalOptions = { entry, type: 'duplicate' };
  }

  @action
  onDuplicateAsOtherType(entry: BudgetForecastEntry): void {
    this.duplicateModalOptions = { entry, type: 'duplicate-as-other-type' };
  }

  @action
  onOpenCreateEntryModal(): void {
    this.isCreateEntryModalOpen = true;
  }

  @action
  onOpenDeleteEntryModal(entry: BudgetForecastEntry): void {
    this.entryBeingDeleted = entry;
  }

  @action
  onOpenEditTitleModal(entry: BudgetForecastEntry): void {
    this.entryBeingTitleEdited = entry;
  }

  @action
  onSelectTab(tabId: string): void {
    if (!isValidTabId(tabId)) {
      return;
    }

    this.activeTabId = tabId;
  }

  @action
  validateBudgetForecastTitle(title: string): boolean {
    const existingTitles = new Set(
      this.args.entries.map((e) => e.titleWithDefault.trim().toLowerCase()),
    );
    return !existingTitles.has(title.trim().toLowerCase());
  }

  <template>
    {{#if @isLoading}}
      <PrismLoadingBar />
    {{/if}}

    <SettingsPageTemplate
      @description={{if
        (eq @view "budget")
        (t "settings.budget.index.description")
        (t "settings.forecast.index.description")
      }}
      @hasAccountSelector={{true}}
      @title={{if
        (eq @view "budget")
        (t "settings.budget.index.title")
        (t "settings.forecast.index.title")
      }}
    >
      <:headerAfter>
        <PrismButton
          @iconBefore="plus"
          @isDisabled={{@isLoading}}
          @label={{if
            (eq @view "budget")
            (t "budget_forecast.create_budget")
            (t "budget_forecast.create_forecast")
          }}
          @onClick={{this.onOpenCreateEntryModal}}
        />
      </:headerAfter>
      <:default>
        <PrismCard @contentLayout="noPadding" class="overflow-hidden">
          <:content>
            <PrismTabsContainer
              @activeTabId={{this.activeTabId}}
              @onSelectTab={{this.onSelectTab}}
              @visibleTabIds={{array TABS.ACTIVE TABS.ARCHIVE}}
              as |TabsContainer|
            >
              <TabsContainer.header>
                <:default as |Header|>
                  <Header.tab @id={{TABS.ACTIVE}} data-test-tab="active">
                    {{t "budget_forecast.active"}}
                  </Header.tab>
                  <Header.tab @id={{TABS.ARCHIVE}} data-test-tab="archive">
                    {{t "budget_forecast.archived"}}
                  </Header.tab>
                </:default>
              </TabsContainer.header>
              <TabsContainer.content as |Content|>
                {{! template-lint-disable no-duplicate-id }}
                <Content.tab @id={{TABS.ACTIVE}}>
                  <SettingsBudgetForecastTableEntries
                    @entries={{this.activeEntries}}
                    @isLoading={{@isLoading}}
                    @onArchiveClick={{this.archiveEntryTask.perform}}
                    @onCopyToExisting={{this.onCopyToExisting}}
                    @onDeleteEntry={{this.onOpenDeleteEntryModal}}
                    @onDuplicateEntry={{this.onDuplicateAs}}
                    @onDuplicateEntryAsOtherType={{this.onDuplicateAsOtherType}}
                    @onEditTitle={{this.onOpenEditTitleModal}}
                    @onLockEntry={{this.lockEntryTask.perform}}
                    @onUnlockEntry={{this.unlockEntryTask.perform}}
                    @view={{@view}}
                  />
                </Content.tab>

                <Content.tab @id={{TABS.ARCHIVE}}>
                  <SettingsBudgetForecastTableEntries
                    @entries={{this.archivedEntries}}
                    @isLoading={{@isLoading}}
                    @onCopyToExisting={{this.onCopyToExisting}}
                    @onDeleteEntry={{this.onOpenDeleteEntryModal}}
                    @onDuplicateEntry={{this.onDuplicateAs}}
                    @onDuplicateEntryAsOtherType={{this.onDuplicateAsOtherType}}
                    @onEditTitle={{this.onOpenEditTitleModal}}
                    @onLockEntry={{this.lockEntryTask.perform}}
                    @onRestoreEntry={{this.restoreEntryTask.perform}}
                    @onUnlockEntry={{this.unlockEntryTask.perform}}
                    @view={{@view}}
                  />

                  {{#if (eq this.archivedEntries.length 0)}}
                    <div class="flex items-center justify-center">
                      <PrismMessage
                        @description={{if
                          (eq @view "budget")
                          (t
                            "budget_forecast.archive.empty_state.budget.description"
                          )
                          (t
                            "budget_forecast.archive.empty_state.forecast.description"
                          )
                        }}
                        @illustration="100x100_empty"
                        @title={{if
                          (eq @view "budget")
                          (t "budget_forecast.archive.empty_state.budget.title")
                          (t
                            "budget_forecast.archive.empty_state.forecast.title"
                          )
                        }}
                        data-test-empty-state
                      />
                    </div>
                  {{/if}}
                </Content.tab>
              </TabsContainer.content>
            </PrismTabsContainer>
          </:content>
        </PrismCard>
      </:default>
    </SettingsPageTemplate>

    {{#if this.isCreateEntryModalOpen}}
      <SettingsBudgetForecastModalsModalCreateEntry
        @isTitleUnique={{this.validateBudgetForecastTitle}}
        @onClose={{this.onCloseCreateEntryModal}}
        @view={{@view}}
      />
    {{/if}}

    {{#if this.entryBeingTitleEdited}}
      <SettingsBudgetForecastModalsModalEditEntryTitle
        @entry={{this.entryBeingTitleEdited}}
        @isTitleUnique={{this.validateBudgetForecastTitle}}
        @onClose={{this.onCloseEditTitleModal}}
      />
    {{/if}}

    {{#if this.entryBeingDeleted}}
      <SettingsBudgetForecastModalsModalDeleteEntry
        @entry={{this.entryBeingDeleted}}
        @onClose={{this.onCloseDeleteEntryModal}}
      />
    {{/if}}

    {{#if this.duplicateModalOptions}}
      <SettingsBudgetForecastModalsModalDuplicateEntry
        @entry={{this.duplicateModalOptions.entry}}
        @isDuplicateAsOtherType={{eq
          this.duplicateModalOptions.type
          "duplicate-as-other-type"
        }}
        @isTitleUnique={{this.validateBudgetForecastTitle}}
        @onClose={{this.onCloseDuplicateModal}}
      />
    {{/if}}

    {{#if this.entryBeingCopiedToExisting}}
      <SettingsBudgetForecastModalsModalCopyToExisting
        @entry={{this.entryBeingCopiedToExisting}}
        @onClose={{this.onCloseCopyToExistingModal}}
      />
    {{/if}}
  </template>
}
